import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  saveUserProfile, useUserProfile, emptyProfile,
  SPORTS, CONTENT_TYPES, COMMERCIAL_INTERESTS, FOODS, BEVERAGES,
  BRANDS_HINTS, DELIVERY_APPS, SPONSOR_CATEGORIES, FAN_TYPES,
  type UserProfile,
} from "@/lib/pr21/userProfile";
import { AvatarUpload } from "@/components/pr21/AvatarUpload";
import { ThemeSelector } from "@/components/pr21/ThemeSelector";
import { trackEvent } from "@/lib/audit";
import { RouteTopBar } from "@/components/layout/RouteTopBar";
import { useLanguage } from "@/hooks/useLanguage";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/onboarding")({
  head: () => ({
    meta: [
      { title: "Onboarding · VerseSport™" },
      { name: "description", content: "Configura tu perfil de fan en 7 pasos rápidos." },
    ],
  }),
  component: OnboardingPage,
});

const STEP_KEYS = [
  "onb_step_identity",
  "onb_step_consent",
] as const;

function OnboardingPage() {
  const { t } = useLanguage();
  const profile = useUserProfile();
  const [draft, setDraft] = useState<UserProfile>(() => profile ?? emptyProfile());
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const stepLabel = (i: number) => t(STEP_KEYS[i]);

  const update = (patch: Partial<UserProfile>) => setDraft((d) => ({ ...d, ...patch }));

  const next = () => {
    void trackEvent("onboarding_step_completed", { step, label: stepLabel(step) });
    saveUserProfile(draft);
    if (step < STEP_KEYS.length - 1) setStep((s) => s + 1);
    else finish();
  };

  const finish = () => {
    const now = new Date().toISOString();
    saveUserProfile({
      ...draft,
      onboarding_completed: true,
      terms_accepted_at: draft.terms_accepted_at ?? now,
      privacy_accepted_at: draft.privacy_accepted_at ?? now,
    });
    void trackEvent("onboarding_completed", { source: "wizard" });
    toast.success("Perfil completado");
    try { window.localStorage.setItem("ss2026_registered", "true"); } catch { /* noop */ }
    void navigate({ to: "/" });
  };

  const progress = useMemo(() => Math.round(((step + 1) / STEP_KEYS.length) * 100), [step]);

  return (
    <main className="min-h-screen bg-background">
      <RouteTopBar fallback="/" />
      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
        <header className="mb-4">
          <h1 className="text-2xl font-black">{t("onb_title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("onb_step_label", { current: step + 1, total: STEP_KEYS.length, label: stepLabel(step) })}
          </p>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-accent/20">
            <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        </header>

        <section className="rounded-xl border border-border bg-accent/5 p-4">
          {step === 0 && <StepAvatarTheme draft={draft} update={update} />}
          {step === 1 && <StepConsent draft={draft} update={update} />}
        </section>
        <p className="mt-3 text-center text-[11px] text-muted-foreground">
          Solo lo esencial. Puedes completar el resto cuando quieras desde{" "}
          <Link to="/ajustes/privacidad" className="font-bold text-primary hover:underline">Ajustes</Link>.
        </p>

        <div className="mt-4 flex items-center justify-between gap-2">
          <button type="button" disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="rounded-md border border-border px-3 py-2 text-sm disabled:opacity-40">{t("onb_back")}</button>
          <div className="flex gap-2">
            <button type="button" onClick={() => { saveUserProfile(draft); finish(); }}
              className="rounded-md border border-border px-3 py-2 text-xs">{t("onb_skip")}</button>
            <button type="button" onClick={next}
              className="rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground">
              {step === STEP_KEYS.length - 1 ? t("onb_finish") : t("onb_continue")}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}

// ---------- step components ---------------------------------------------

type StepProps = { draft: UserProfile; update: (p: Partial<UserProfile>) => void };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

const inputCls = "w-full rounded-md border border-border bg-background px-3 py-2 text-sm";

function ChipGroup({ values, options, onToggle }: { values: string[]; options: string[]; onToggle: (v: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const active = values.includes(o);
        return (
          <button key={o} type="button" onClick={() => onToggle(o)}
            className={`rounded-full px-2.5 py-1 text-xs font-bold transition ${active ? "bg-primary text-primary-foreground" : "bg-accent/30 text-foreground"}`}>
            {o.replace(/_/g, " ")}
          </button>
        );
      })}
    </div>
  );
}

function toggle(list: string[], v: string) {
  return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
}

function StepAccount({ draft, update }: StepProps) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nombre"><input className={inputCls} value={draft.first_name} onChange={(e) => update({ first_name: e.target.value })} /></Field>
        <Field label="Apellido"><input className={inputCls} value={draft.last_name} onChange={(e) => update({ last_name: e.target.value })} /></Field>
      </div>
      <Field label="Email"><input type="email" className={inputCls} value={draft.email} onChange={(e) => update({ email: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="País"><input className={inputCls} value={draft.country} onChange={(e) => update({ country: e.target.value })} /></Field>
        <Field label="Idioma">
          <select className={inputCls} value={draft.language} onChange={(e) => update({ language: e.target.value as UserProfile["language"] })}>
            <option value="ES">Español</option>
            <option value="EN">English</option>
            <option value="PT">Português</option>
          </select>
        </Field>
      </div>
    </div>
  );
}

function StepFan({ draft, update }: StepProps) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Año de nacimiento">
          <input type="number" min={1920} max={new Date().getFullYear() - 5} className={inputCls}
            value={draft.birth_year ?? ""} onChange={(e) => update({ birth_year: e.target.value ? Number(e.target.value) : null })} />
        </Field>
        <Field label="Género">
          <select className={inputCls} value={draft.gender} onChange={(e) => update({ gender: e.target.value as UserProfile["gender"] })}>
            <option value=""></option>
            <option value="female">Femenino</option>
            <option value="male">Masculino</option>
            <option value="nonbinary">No binario</option>
            <option value="prefer_not">Prefiero no decir</option>
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Ciudad"><input className={inputCls} value={draft.city} onChange={(e) => update({ city: e.target.value })} /></Field>
        <Field label="Equipo favorito (club)"><input className={inputCls} value={draft.favorite_team} onChange={(e) => update({ favorite_team: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Selección favorita"><input className={inputCls} value={draft.favorite_national_team} onChange={(e) => update({ favorite_national_team: e.target.value })} /></Field>
        <Field label="Jugador favorito"><input className={inputCls} value={draft.favorite_player} onChange={(e) => update({ favorite_player: e.target.value })} /></Field>
      </div>
      <Field label="Tipo de fan">
        <ChipGroup values={draft.fan_type ? [draft.fan_type] : []} options={FAN_TYPES} onToggle={(v) => update({ fan_type: v as UserProfile["fan_type"] })} />
      </Field>
    </div>
  );
}

function StepPrefs({ draft, update }: StepProps) {
  return (
    <div className="space-y-3">
      <Field label="Deportes favoritos"><ChipGroup values={draft.favorite_sports} options={SPORTS} onToggle={(v) => update({ favorite_sports: toggle(draft.favorite_sports, v) })} /></Field>
      <Field label="Contenido preferido"><ChipGroup values={draft.favorite_content} options={CONTENT_TYPES} onToggle={(v) => update({ favorite_content: toggle(draft.favorite_content, v) })} /></Field>
      <Field label="Frecuencia de notificaciones">
        <select className={inputCls} value={draft.notification_frequency} onChange={(e) => update({ notification_frequency: e.target.value as UserProfile["notification_frequency"] })}>
          <option value="high">Alta</option>
          <option value="medium">Media</option>
          <option value="low">Baja</option>
          <option value="off">Apagadas</option>
        </select>
      </Field>
      <Field label="Intereses comerciales"><ChipGroup values={draft.commercial_interests} options={COMMERCIAL_INTERESTS} onToggle={(v) => update({ commercial_interests: toggle(draft.commercial_interests, v) })} /></Field>
    </div>
  );
}

function StepConsumption({ draft, update }: StepProps) {
  return (
    <div className="space-y-3">
      <Field label="Comidas favoritas"><ChipGroup values={draft.favorite_foods} options={FOODS} onToggle={(v) => update({ favorite_foods: toggle(draft.favorite_foods, v) })} /></Field>
      <Field label="Bebidas favoritas"><ChipGroup values={draft.favorite_beverages} options={BEVERAGES} onToggle={(v) => update({ favorite_beverages: toggle(draft.favorite_beverages, v) })} /></Field>
      <Field label="Marcas preferidas"><ChipGroup values={draft.favorite_brands} options={BRANDS_HINTS} onToggle={(v) => update({ favorite_brands: toggle(draft.favorite_brands, v) })} /></Field>
      <Field label="Apps de delivery"><ChipGroup values={draft.delivery_apps} options={DELIVERY_APPS} onToggle={(v) => update({ delivery_apps: toggle(draft.delivery_apps, v) })} /></Field>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={draft.coupon_interest} onChange={(e) => update({ coupon_interest: e.target.checked })} />
        Quiero recibir cupones y descuentos
      </label>
    </div>
  );
}

function StepSponsors({ draft, update }: StepProps) {
  return (
    <div className="space-y-3">
      <Field label="Categorías de patrocinadores"><ChipGroup values={draft.sponsor_categories} options={SPONSOR_CATEGORIES} onToggle={(v) => update({ sponsor_categories: toggle(draft.sponsor_categories, v) })} /></Field>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={draft.accepts_promotions} onChange={(e) => update({ accepts_promotions: e.target.checked })} />
        Acepto promociones de patrocinadores
      </label>
      <Field label="Preferencia de recompensas">
        <select className={inputCls} value={draft.reward_preference} onChange={(e) => update({ reward_preference: e.target.value as UserProfile["reward_preference"] })}>
          <option value="">—</option>
          <option value="vouchers">Cupones</option>
          <option value="physical">Físicas</option>
          <option value="points">Puntos</option>
          <option value="experiences">Experiencias</option>
        </select>
      </Field>
    </div>
  );
}

function StepAvatarTheme({ draft, update }: StepProps) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-black">Elige tu identidad futbolística</h2>
        <p className="text-xs text-muted-foreground">
          Tu equipo pinta toda la experiencia: glow, gradientes, acentos y narrativa.
        </p>
      </div>
      <AvatarUpload value={draft.avatar_url} onChange={(v) => update({ avatar_url: v })} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nombre visible"><input className={inputCls} value={draft.display_name} onChange={(e) => update({ display_name: e.target.value })} /></Field>
        <Field label="Username"><input className={inputCls} value={draft.username} onChange={(e) => update({ username: e.target.value.replace(/\s+/g, "").toLowerCase() })} /></Field>
      </div>
      <Field label="Tu equipo"><ThemeSelector /></Field>
    </div>
  );
}

function StepConsent({ draft, update }: StepProps) {
  return (
    <div className="space-y-3 text-xs">
      <p className="text-sm text-muted-foreground">
        Cumplimos con GDPR / CCPA. Puedes administrar tus preferencias en cualquier momento.
      </p>
      <label className="flex items-start gap-2 rounded-md border border-border/60 bg-accent/10 p-3">
        <input
          type="checkbox"
          defaultChecked={!!draft.terms_accepted_at}
          onChange={(e) => {
            const ts = e.target.checked ? new Date().toISOString() : null;
            update({ terms_accepted_at: ts, privacy_accepted_at: ts });
          }}
        />
        <span className="text-sm">
          Acepto los{" "}
          <Link to="/terminos" className="font-bold text-primary hover:underline">Términos</Link>{" "}y la{" "}
          <Link to="/privacidad" className="font-bold text-primary hover:underline">Política de Privacidad</Link>.
        </span>
      </label>
      <p className="text-[11px] text-muted-foreground">
        Administra marketing, notificaciones y experiencias patrocinadas en{" "}
        <Link to="/ajustes/privacidad" className="font-bold text-primary hover:underline">
          Privacidad y preferencias
        </Link>
        .
      </p>
    </div>
  );
}